/**
 * selectors/auth.js — identité/rôles : source UNIQUE des flags d'accès (vague 4 lot 3).
 *
 * Remplace le recalcul par JSON.parse(localStorage.getItem('_user')) répété ~10 fois
 * par render dans pages/index.tsx (et dupliqué dans 24 fichiers — 42 occurrences),
 * chacun avec sa propre variante de formule (== 1, === 1, isVerified vs accessToken…).
 *
 * Flux normal désormais (remplace le hack useSelector du bug nav 2026-06-18) :
 *   login/logout/refresh → utils/auth/session.js (persistUser/clearUser) → écrit
 *   localStorage._user ET dispatch(setUser) → ce selector recalcule → tout composant
 *   abonné (routes de pages/index.tsx, menus header, écrans denied) re-rend avec les
 *   bons flags, sans reload ni lecture storage. L'hydratation au boot est faite une
 *   seule fois dans store/index.js (readStoredUser).
 *
 * Sémantiques préservées à l'identique (source : pages/index.tsx:112-137 historique) :
 *  - isAuthenticated = PRÉSENCE DE SESSION (accessToken), PAS isVerified
 *    (FIX 2026-06-30 : depuis SPEC-2026-06-29-auth-confirm-2fa.v1 un compte wallet
 *    connecté peut être isVerified=false — navigation libre, actions gatées serveur) ;
 *  - isVerified reste exposé BRUT (true/false/undefined) : ModalRegister teste
 *    `isVerified === false` strictement (visiteur → undefined → pas de modal) ;
 *  - comparaisons de rôle par NOM via l'enum EROLES (plus de littéraux 1/2/3).
 */
import { createSelector } from 'reselect';
import { EROLES } from '../../utils/constants/roles.enum';

/** User de session courant (objet vide = visiteur). */
export const selectUser = (state) => (state && state.user && state.user.user) || {};

/**
 * Les 8 flags d'accès, mémoïsés : recalculés uniquement quand state.user.user change
 * (référence), et l'objet retourné garde la même référence entre deux renders sinon.
 */
export const selectAuthFlags = createSelector([selectUser], (u) => {
	// Miroir de l'ex-garde « localStorage.getItem('_user') && … » : un objet user vide
	// (visiteur / logout → setUser({})) donne TOUS les flags à false.
	const hasUser = !!u && typeof u === 'object' && Object.keys(u).length > 0;
	const roleId = hasUser ? Number(u.roleId) : NaN;
	const isAdmin = hasUser && roleId === EROLES.ADMIN;
	return {
		isAuthenticated: hasUser && !!u.accessToken,
		isAdmin,
		// Créateur = tout rôle non-STANDARD (AUTHOR ou ADMIN) — aligné menu "My Collections".
		isCreator: hasUser && roleId !== EROLES.STANDARD,
		isSupport: hasUser && (u.isSupport === true || isAdmin),
		isModerator: hasUser && (u.isModerator === true || isAdmin),
		isCollaborator: hasUser && (u.isCollaborator === true || u.isModerator === true || isAdmin),
		// Collaborateur UE (équipe jeu / CTO) : accès /api-docs en vue Swagger filtrée UE.
		isCollaboratorUE: hasUser && (u.isCollaboratorUE === true || u.isCollaborator === true || u.isModerator === true || isAdmin),
		isSnapshot: hasUser && !!u.isSnapshot,
		// BRUT exprès (true | false | undefined) — cf. en-tête.
		isVerified: hasUser ? u.isVerified : undefined,
	};
});
